import { useCallback, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { computeLaplacianVariance, CAMERA_MAX_SIZE, BLUR_THRESHOLD, COUNTDOWN_SECONDS } from './imageUtils';

export interface UseEnhancedWebcamOptions {
  /** Called when user confirms the captured photo (taps "Use Photo") */
  onUsePhoto: (file: File, previewUrl: string) => void;
}

export interface UseEnhancedWebcamReturn {
  webcamRef: React.MutableRefObject<Webcam | null>;
  showWebcam: boolean;
  webcamPreview: string | null;
  webcamCountdown: number | null;
  isWebcamCapturing: boolean;
  webcamBlurWarning: string | null;
  lastSharpnessRef: React.MutableRefObject<number | null>;
  handleOpenCamera: () => void;
  handleCloseWebcam: () => void;
  startWebcamCountdown: () => void;
  handleWebcamRetake: () => void;
  handleWebcamUsePhoto: () => void;
}

export function useEnhancedWebcam({ onUsePhoto }: UseEnhancedWebcamOptions) {
  const webcamRef = useRef<Webcam>(null!);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamCountdown, setWebcamCountdown] = useState<number | null>(null);
  const [isWebcamCapturing, setIsWebcamCapturing] = useState(false);
  const [webcamBlurWarning, setWebcamBlurWarning] = useState<string | null>(null);
  const [webcamPreview, setWebcamPreview] = useState<string | null>(null);
  const [webcamCapturedFile, setWebcamCapturedFile] = useState<File | null>(null);
  const webcamCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSharpnessRef = useRef<number | null>(null);

  const handleOpenCamera = useCallback(() => {
    setWebcamBlurWarning(null);
    setWebcamPreview(null);
    setWebcamCapturedFile(null);
    lastSharpnessRef.current = null;
    setShowWebcam(true);
  }, []);

  const handleCloseWebcam = useCallback(() => {
    if (webcamCountdownRef.current) {
      clearInterval(webcamCountdownRef.current);
      webcamCountdownRef.current = null;
    }
    if (webcamPreview) URL.revokeObjectURL(webcamPreview);
    setWebcamCountdown(null);
    setIsWebcamCapturing(false);
    setWebcamBlurWarning(null);
    setWebcamPreview(null);
    setWebcamCapturedFile(null);
    setShowWebcam(false);
  }, [webcamPreview]);

  /**
   * Attempt native ImageCapture API for sharper still photos.
   * Falls back to canvas screenshot if unavailable.
   */
  const tryImageCapture = useCallback(async (): Promise<Blob | null> => {
    try {
      const stream = webcamRef.current?.video?.srcObject as MediaStream | null;
      if (!stream) {
        console.debug("[useEnhancedWebcam] No stream for ImageCapture, falling back to canvas");
        return null;
      }

      const [videoTrack] = stream.getVideoTracks();
      if (!videoTrack) return null;

      const imageCapture = new (window as any).ImageCapture(videoTrack);
      const bitmap = await imageCapture.grabFrame();

      // Draw bitmap to canvas for consistency with blur detection
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(bitmap, 0, 0);
      console.info("[useEnhancedWebcam] Used native ImageCapture API for sharper frame");
      return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    } catch (err) {
      console.debug("[useEnhancedWebcam] ImageCapture not supported or failed, will use canvas screenshot:", err);
      return null;
    }
  }, []);

  const handleWebcamCapture = useCallback(() => {
    setWebcamBlurWarning(null);

    // Get the visible crop area based on object-fit: cover
    const videoEl = webcamRef.current?.video;
    const containerWidth = videoEl?.clientWidth || 1;
    const containerHeight = videoEl?.clientHeight || 1;

    const containerAspect = containerWidth / containerHeight;

    console.info("[useEnhancedWebcam] Container:", { containerWidth, containerHeight, containerAspect });

    // Try ImageCapture first (sharper), fall back to canvas screenshot
    tryImageCapture().then(async (nativeBlob) => {
      let imageSrc: string | null = null;

      if (nativeBlob) {
        // Use native ImageCapture result
        imageSrc = URL.createObjectURL(nativeBlob);
      } else {
        // Fall back to canvas screenshot
        imageSrc = webcamRef.current?.getScreenshot();
        if (!imageSrc) {
          setIsWebcamCapturing(false);
          return;
        }
      }

      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setIsWebcamCapturing(false);
          return;
        }

        const imgW = image.naturalWidth;
        const imgH = image.naturalHeight;
        const imgAspect = imgW / imgH;

        // Calculate crop to match the frame area (object-fit: cover logic)
        let cropX = 0, cropY = 0, cropW = imgW, cropH = imgH;
        if (imgAspect > containerAspect) {
          // Image is wider than frame — crop sides
          cropW = Math.round(imgH * containerAspect);
          cropX = Math.round((imgW - cropW) / 2);
        } else {
          // Image is taller than frame — crop top/bottom
          cropH = Math.round(imgW / containerAspect);
          cropY = Math.round((imgH - cropH) / 2);
        }

        console.info("[useEnhancedWebcam] Crop to frame:", { cropX, cropY, cropW, cropH, imgW, imgH });

        // Crop to only the content within the guide frame
        canvas.width = cropW;
        canvas.height = cropH;
        ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
        
        // Sharpness check — reject blurry images
        const sharpness = computeLaplacianVariance(canvas);
        lastSharpnessRef.current = sharpness;
        console.info("[useEnhancedWebcam] Image sharpness score:", Math.round(sharpness), "threshold:", BLUR_THRESHOLD);
        if (sharpness < BLUR_THRESHOLD) {
          const msg = `Image too blurry. Please hold camera steady and try again or use upload option.`;
          console.warn("[useEnhancedWebcam]", msg);
          setWebcamBlurWarning(msg);
          setIsWebcamCapturing(false);
          return;
        }

        canvas.toBlob((finalBlob) => {
          if (!finalBlob) {
            setIsWebcamCapturing(false);
            return;
          }

          if (finalBlob.size > CAMERA_MAX_SIZE) {
            setWebcamBlurWarning("Captured image is too large. Please try again.");
            setIsWebcamCapturing(false);
            return;
          }

          const file = new File([finalBlob], `photo_${Date.now()}.png`, { type: "image/png" });
          const previewUrl = URL.createObjectURL(finalBlob);
          setWebcamCapturedFile(file);
          setWebcamPreview(previewUrl);
          setIsWebcamCapturing(false);

          // Clean up temporary blob if from ImageCapture
          if (imageSrc?.startsWith('blob:')) {
            URL.revokeObjectURL(imageSrc);
          }
        }, 'image/png');
      };
    }).catch((err) => {
      console.error("[useEnhancedWebcam] Capture error:", err);
      setIsWebcamCapturing(false);
    });
  }, [tryImageCapture]);

  const startWebcamCountdown = useCallback(() => {
    setWebcamBlurWarning(null);
    setIsWebcamCapturing(true);
    setWebcamCountdown(COUNTDOWN_SECONDS);

    let remaining = COUNTDOWN_SECONDS;
    webcamCountdownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        if (webcamCountdownRef.current) clearInterval(webcamCountdownRef.current);
        setWebcamCountdown(null);
        handleWebcamCapture();
      } else {
        setWebcamCountdown(remaining);
      }
    }, 1000);
  }, [handleWebcamCapture]);

  const handleWebcamRetake = useCallback(() => {
    if (webcamPreview) URL.revokeObjectURL(webcamPreview);
    setWebcamPreview(null);
    setWebcamCapturedFile(null);
    setWebcamBlurWarning(null);
  }, [webcamPreview]);

  const handleWebcamUsePhoto = useCallback(() => {
    if (!webcamCapturedFile || !webcamPreview) return;
    onUsePhoto(webcamCapturedFile, webcamPreview);
    setWebcamPreview(null);
    setWebcamCapturedFile(null);
    setShowWebcam(false);
  }, [webcamCapturedFile, webcamPreview, onUsePhoto]);

  return {
    webcamRef,
    showWebcam,
    webcamPreview,
    webcamCountdown,
    isWebcamCapturing,
    webcamBlurWarning,
    lastSharpnessRef,
    handleOpenCamera,
    handleCloseWebcam,
    startWebcamCountdown,
    handleWebcamRetake,
    handleWebcamUsePhoto,
  };
}
